import { useRef, useCallback } from 'react';
import { API } from '../config';
import { apiFetch } from '../deviceId';
import { createParticipantBatcher } from '../lib/participantBatch';

async function postJson(url, body) {
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return response.json();
}

/**
 * Participant tracking hook.
 *
 * register / status / telemetry are routed through the gateway batcher
 * (buffered + flushed on a desynchronised rolling timer, with HTTP fallback)
 * so the synchronised playing/completed bursts stay off the Galaxy write-path.
 * zone / opt-out stay on direct HTTP — they're user-paced / rare, not bursts,
 * and zone's HTTP path keeps its Mongo-validated show↔pipe check.
 *
 * The method surface is unchanged (all return promises) so call sites that
 * `await` or `.catch()` keep working.
 */
export function useParticipant() {
  const batcher = useRef(createParticipantBatcher()).current;
  const deviceId = batcher.deviceId;
  const registeredShowRef = useRef(null);

  const register = useCallback(
    async (pipeId, showInstanceId) => {
      if (registeredShowRef.current === showInstanceId) return;
      registeredShowRef.current = showInstanceId;
      batcher.register(pipeId, showInstanceId);
    },
    [batcher]
  );

  const updateStatus = useCallback(
    async (pipeId, showInstanceId, status, metadata = {}) => {
      batcher.status(pipeId, showInstanceId, status, metadata);
    },
    [batcher]
  );

  const sendTelemetry = useCallback(
    async (pipeId, showInstanceId, telemetryData) => {
      batcher.telemetry(pipeId, showInstanceId, telemetryData);
    },
    [batcher]
  );

  const heartbeat = useCallback(
    (pipeId, showInstanceId) => {
      batcher.heartbeat(pipeId, showInstanceId);
    },
    [batcher]
  );

  const selectZone = useCallback(
    async (pipeId, showInstanceId, zoneIndex, rowIndex = null, syncOffset = null) => {
      await postJson(API.participant.zone(), {
        pipeId,
        showInstanceId,
        deviceId,
        zoneIndex,
        ...(rowIndex !== null ? { rowIndex } : {}),
        ...(syncOffset !== null ? { syncOffset } : {}),
      });
    },
    [deviceId]
  );

  const optOut = useCallback(
    async (pipeId, showInstanceId) => {
      await postJson(API.participant.optout(), {
        pipeId,
        showInstanceId,
        deviceId,
      });
    },
    [deviceId]
  );

  /**
   * Hand every buffered event to sendBeacon so it survives navigation.
   * Called immediately before the post-show redirect (MM #111).
   */
  const flushBeacon = useCallback(() => batcher.flushBeacon(), [batcher]);

  return {
    deviceId,
    flushBeacon,
    register,
    updateStatus,
    sendTelemetry,
    heartbeat,
    selectZone,
    optOut,
  };
}
